import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';

export interface BlastJobDto {
  jobId: number;
  userId: number;
  blastId: number;
  startDate: Date;
  endDate?: Date;
  importStatus?: ImportStatus;
  userFirstName?: string;
  userLastName?: string;
}

export interface ImportStatus {
  status: boolean;
  totalEvents: number;
  executionTimeMs?: number;
  errors: string[];
  filePath?: string;
  sourceType: any; // Permet à la fois number, string, et SourceType
}

export enum SourceType {
  StrFile = 0,
  Expertir = 1,
  Expertab = 2,
  Other = 3
}

@Injectable({
  providedIn: 'root'
})
export class GeneralService {

  constructor(private readonly http: HttpService) { }

  /**
   * Récupère l'historique des jobs de blast pour un blast donné
   * @param blastId ID du blast
   * @returns Observable contenant la liste des jobs
   */
  getBlastJobHistory(blastId: number): Observable<BlastJobDto[]> {
    return this.http.get<BlastJobDto[]>(`Blasts/${blastId}/blast-job-history`);
  }

  /**
   * Détermine la classe CSS pour le statut basé sur l'ImportStatus
   * @param importStatus Objet ImportStatus
   * @returns Classe CSS pour le statut
   */
  getStatusClass(importStatus?: ImportStatus): string {
    if (!importStatus) return 'status-unknown';
    
    // Succès partiel : status true mais avec des erreurs
    if (importStatus.status && importStatus.errors && importStatus.errors.length > 0) {
      return 'status-partial';
    }
    
    // Succès complet ou échec
    return importStatus.status ? 'status-success' : 'status-error';
  }

  /**
   * Obtient le nom d'affichage pour le type de source
   * @param importStatus Objet ImportStatus
   * @returns Nom d'affichage du type de source
   */
  getSourceTypeDisplay(importStatus?: ImportStatus): string {
    if (!importStatus) {
      console.log('ImportStatus is null or undefined');
      return 'Unknown';
    }
    
    console.log('ImportStatus received:', importStatus);
    console.log('SourceType value:', importStatus.sourceType, 'Type:', typeof importStatus.sourceType);
    
    const sourceType = importStatus.sourceType;
    
    // Convertir en string pour comparaison uniforme
    const sourceTypeStr = String(sourceType).toLowerCase();
    
    switch (sourceTypeStr) {
      case '0':
      case 'strfile':
        return 'STR File';
      case '1':
      case 'expertir':
        return 'Expertir';
      case '2':
      case 'expertab':
        return 'Expertab';
      case '3':
      case 'other':
        return 'Other';
      default:
        console.log('SourceType not matched, defaulting to Other. Value was:', sourceType);
        return 'Other (' + sourceType + ')'; // Afficher la valeur pour debug
    }
  }

  /**
   * Formate une date en format français avec heure
   * @param date Date à formater
   * @returns Chaîne formatée ou chaîne vide si date invalide
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Formate le nom complet de l'utilisateur
   * @param job Objet BlastJobDto contenant les informations utilisateur
   * @returns Nom complet formaté ou ID utilisateur si noms indisponibles
   */
  formatUserName(job: BlastJobDto): string {
    if (job.userFirstName || job.userLastName) {
      const firstName = job.userFirstName || '';
      const lastName = job.userLastName || '';
      return `${firstName} ${lastName}`.trim();
    }
    return `User ${job.userId}`;
  }
}